import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import type {ScannerState, KioskoConnectionState, PrinterState} from './types';

interface KioskoState {
  connection: {
    isConnected: boolean;
    isConnecting: boolean;
    error: Error | null;
    isInitializing: boolean;
    hasCompletedInitialization: boolean;
  };
  scanner: ScannerState;
  printer: PrinterState;
  cards: {
    printed: number;
    pending: number;
  };
  apiKey?: string;
}

interface KioskoActions {
  setConnectionState: (state: Partial<KioskoConnectionState>) => void;
  setScannerState: (state: Partial<ScannerState>) => void;
  resetScannerState: () => void;
  setApiKey: (apiKey: string) => void;
  setPrinterState: (state: Partial<PrinterState>) => void;
  setCardsState: (state: Partial<{printed: number; pending: number}>) => void;
  resetPrinterState: () => void;
}

interface KioskoStore extends KioskoState, KioskoActions {}

const DEFAULT_PRINTER_STATE: PrinterState = {
  isPrinting: false,
  isPrintingError: false,
  isPrintingSuccess: false,
};

const INITIAL_STATE: KioskoState = {
  connection: {
    isConnected: false,
    isConnecting: false,
    error: null,
    isInitializing: false,
    hasCompletedInitialization: false,
  },
  scanner: {
    isInitialized: true,
    lastScanResult: null,
    isScanning: false,
    success: false,
    error: '',
  },
  apiKey: undefined,
  printer: DEFAULT_PRINTER_STATE,
  cards: {
    printed: 0,
    pending: 0,
  },
};

export const useKioskoStore = create<KioskoStore>()(
  persist(
    set => ({
      ...INITIAL_STATE,

      setConnectionState: connectionState =>
        set(state => ({
          connection: {...state.connection, ...connectionState},
        })),

      setScannerState: scannerState =>
        set(state => ({
          scanner: {...state.scanner, ...scannerState},
        })),

      resetScannerState: () =>
        set(state => ({
          scanner: {
            ...state.scanner,
            lastScanResult: null,
            isScanning: false,
          },
        })),

      setApiKey: apiKey => set({apiKey}),

      setPrinterState: printerState =>
        set(state => ({
          printer: {...state.printer, ...printerState},
        })),

      setCardsState: cardsState =>
        set(state => ({
          cards: {...state.cards, ...cardsState},
        })),

      resetPrinterState: () =>
        set(() => ({
          printer: DEFAULT_PRINTER_STATE,
        })),
    }),
    {
      name: 'kiosko-store',
      partialize: state => ({
        cards: state.cards,
      }),
    },
  ),
);
